import React from 'react';

import {
  Grid,
  makeStyles,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import { CategoryScore } from '../../types';
import { GroupedCategoryScore } from '../../utils/scorecardHierarchyUtils';
import { ScorecardInfoCard } from '../ScorecardInfoCard';

const useStyles = makeStyles(theme => ({
  rootAccordion: {
    boxShadow: 'none',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '8px !important',
    marginBottom: theme.spacing(2),
    overflow: 'hidden',
    '&:before': { display: 'none' },
  },
  groupHeader: {
    backgroundColor: theme.palette.background.paper,
    borderLeft: `6px solid ${theme.palette.primary.main}`,
    minHeight: 64,
    '&.Mui-expanded': {
      minHeight: 64,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  groupTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    width: '100%',
  },
  displayName: {
    fontWeight: 700,
    fontSize: '1rem',
    color: theme.palette.text.primary,
  },
  groupScore: {
    padding: '2px 12px',
    borderRadius: 16,
    fontWeight: 700,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
  },
  scoreHigh: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    border: '1px solid #c8e6c9',
  },
  scoreMedium: {
    backgroundColor: '#fff3e0',
    color: '#ef6c00',
    border: '1px solid #ffe0b2',
  },
  scoreLow: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    border: '1px solid #ffcdd2',
  },
  scoreNA: {
    backgroundColor: '#f5f5f5',
    color: '#757575',
    border: '1px solid #e0e0e0',
  },
  nestedAccordion: {
    width: '100%',
    boxShadow: 'none',
    border: 'none',
    borderLeft: `2px solid ${theme.palette.divider}`,
    marginLeft: theme.spacing(1),
    '&:before': { display: 'none' },
  },
  nestedHeader: {
    padding: theme.spacing(0, 2),
    borderLeft: `4px solid ${theme.palette.grey[400]}`,
  },
  accordionDetails: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
  },
  itemCount: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    fontSize: '0.8rem',
  },
}));

interface HierarchicalScorecardViewProps {
  hierarchicalScores: GroupedCategoryScore[];
  onCardClick: (category: CategoryScore) => void;
}

export const HierarchicalScorecardView: React.FC<
  HierarchicalScorecardViewProps
> = ({ hierarchicalScores, onCardClick }) => {
  const classes = useStyles();

  const getScoreClassName = (score: number | 'N/A') => {
    if (score === 'N/A') return classes.scoreNA;
    if (score >= 80) return classes.scoreHigh;
    if (score >= 50) return classes.scoreMedium;
    return classes.scoreLow;
  };

  const renderNode = (
    node: GroupedCategoryScore,
    level: number = 0,
  ): React.ReactNode => {
    if (node.type === 'category' && node.categoryData) {
      return (
        <Grid item xs={12} sm={6} md={4} lg={3} key={node.name}>
          <ScorecardInfoCard
            categoryScore={node.categoryData}
            onCardClick={() => onCardClick(node.categoryData!)}
          />
        </Grid>
      );
    }

    const isRoot = level === 0;

    return (
      <Accordion
        key={node.name}
        defaultExpanded={false}
        className={isRoot ? classes.rootAccordion : classes.nestedAccordion}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          className={isRoot ? classes.groupHeader : classes.nestedHeader}
        >
          <div className={classes.groupTitle}>
            <Typography className={classes.displayName}>
              {node.displayName}
            </Typography>
            <span
              className={`${classes.groupScore} ${getScoreClassName(
                node.score,
              )}`}
            >
              {node.score === 'N/A' ? 'N/A' : `${node.score}%`}
            </span>
            {node.children && (
              <Typography className={classes.itemCount}>
                {node.children.length}{' '}
                {node.children.length === 1 ? 'item' : 'items'}
              </Typography>
            )}
          </div>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
          <Grid container spacing={3}>
            {node.children?.map(child => {
              if (child.type === 'group') {
                return (
                  <Grid item xs={12} key={child.name}>
                    {renderNode(child, level + 1)}
                  </Grid>
                );
              }
              return renderNode(child, level + 1);
            })}
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box>
      {hierarchicalScores.map(node => (
        <React.Fragment key={node.name}>{renderNode(node, 0)}</React.Fragment>
      ))}
    </Box>
  );
};
